import { get } from "@ember/object"
import { A, pipe } from "@mobily/ts-belt"
import type {
  Course,
  CourseCalendarDate,
  CourseCalendarDateCustom,
  CourseCalendarDateDefault,
  CourseDateAnnotation,
  CourseDateAnnotationSingle,
} from "@shared/models/course"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { compact, filter, find, map, uniq } from "lodash-es"

export function isCourseDateCustom(cd: CourseCalendarDate): cd is CourseCalendarDateCustom {
  return (cd as CourseCalendarDateCustom).type === "course-date-custom"
}
export function isCourseDateDefault(cd: CourseCalendarDate): cd is CourseCalendarDateDefault {
  return (cd as CourseCalendarDateDefault).type === "course-date-default"
}

export function isCourseDateAnnotationSingle(
  ann: CourseDateAnnotation
): ann is CourseDateAnnotationSingle {
  return (ann as CourseDateAnnotationSingle).type === "date-annotation-course-custom-single"
}

export function isOnCourseDateDefault(
  course: Course,
  courseDate: CourseCalendarDateDefault
): boolean {
  const id = `${courseDate.attributes.semesterId}:${courseDate.attributes.rotationId}`
  return !course.attributes.calendar.datesOff.includes(id)
}

export function filterCourseDateCustom(course: Course) {
  return pipe(
    get(course.attributes.calendar, "dates"),
    A.filter(isCourseDateCustom),
    A.sortBy((date) => date.attributes.dateString)
  )
}

export function findCourseDateCustom(course: Course, dateString: string) {
  return find(filterCourseDateCustom(course), (date) => date.attributes.dateString === dateString)
}

export function filterCourseDateDefault(course: Course) {
  return filter(get(course.attributes.calendar, "dates"), isCourseDateDefault)
}

export function findCourseDateDefault(course: Course, rotationId: string, semesterId: string) {
  return find(
    filterCourseDateDefault(course),
    (date) => date.attributes.rotationId === rotationId && date.attributes.semesterId === semesterId
  )
}

export function findFirstCourseDateDefault(course: Course, rotationCalendar: RotationCalendar) {
  const sortedRotations = pipe(
    rotationCalendar.attributes.rotations,
    A.sortBy((rotation) => rotation.position)
  )
  if (sortedRotations.length === 0) return undefined
  return find(
    filterCourseDateDefault(course),
    (date) => date.attributes.rotationId === sortedRotations[0].id
  )
}

export function extractCardStackTemplateIds(course: Course): Array<string> {
  // Treat non-instructional courses as ones without a template.
  if (course.attributes.isNonInstructional) return []
  return compact(
    uniq(map(filterCourseDateDefault(course), (date) => get(date.attributes, "cardStackId")))
  )
}
